import React, { useState, useEffect } from 'react';

import { useGetVo2Max } from 'api/services';
import ChartModal from 'components/ChartModal/ChartModal';
import { chartsKey } from 'utils/constants';

import { getChartBgColors } from './utils/chartColorizing';
import {
	handleChartData,
	gradientBgPlugin,
	adaptVo2MaxTable,
	renameObjectsProp,
	getChartMaxValue,
	getChartMinValue,
	defaultChartAxisValues,
	increaseOrDecreaseMaxYThreshold,
} from './utils/chartHelpers';
import {
	defaultConfig,
	ticksCallbackKG,
	tooltipCallbackKG,
	styledChartElements,
} from './utils/customViewChart';

const VO2Max = ({ closeChart, isOpen }) => {
	const [data, setData] = useState({});
	const [tableData, setTableData] = useState(null);
	const [chartConfig, setChartConfig] = useState(null);
	const [labelsData, setLabelsData] = useState(undefined);

	const { data: vox2MaxData, loading } = useGetVo2Max();
	const isDataAvailable = vox2MaxData?.data && vox2MaxData.data.length > 0;
	const lastElement = isDataAvailable && vox2MaxData.data.length - 1;

	useEffect(() => {
		if (isDataAvailable) {
			const refValues = getRefValues(vox2MaxData?.referential_values);
			const dataAdapted = handleChartData(vox2MaxData.data);
			const lastResultVO2Max = vox2MaxData.data[lastElement];

			const tableDataAdapted = adaptVo2MaxTable(
				vox2MaxData?.referential_values,
				lastResultVO2Max,
				vox2MaxData?.age_group
			);

			setData(dataAdapted);
			setTableData(tableDataAdapted);
			setLabelsData(dataAdapted?.labels);
			handleChartConfig(refValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vox2MaxData]);

	const getRefValues = (refValues) =>
		(refValues &&
			refValues.length > 0 &&
			renameObjectsProp(refValues, 'value', 'rating')) ||
		null;

	const handleChartConfig = (refValues) => {
		let minY = defaultChartAxisValues[chartsKey.vo2Max].minY;
		let maxY = defaultChartAxisValues[chartsKey.vo2Max].maxY;

		const minValueOfY = getChartMinValue(vox2MaxData.data, 'value');
		const maxValueOfY = getChartMaxValue(vox2MaxData.data, 'value');

		if (maxValueOfY && maxValueOfY >= maxY) {
			maxY = maxValueOfY + increaseOrDecreaseMaxYThreshold.large;
		}

		if (minValueOfY && minValueOfY <= minY) {
			minY = minValueOfY - increaseOrDecreaseMaxYThreshold.large;
		}

		const chartColors =
			(refValues && getChartBgColors(refValues, maxY, minY)) || [];

		const config = {
			...defaultConfig,
			plugins: {
				...defaultConfig.plugins,
				backgrounds: {
					hbars: chartColors,
				},
				tooltip: {
					...defaultConfig.plugins.tooltip,
					callbacks: { label: tooltipCallbackKG },
				},
			},
			elements: styledChartElements(lastElement),
			scales: {
				...defaultConfig.scales,
				x: {
					...defaultConfig.scales.x,
					ticks: {
						...defaultConfig.scales.x.ticks,
						z: 10,
					},
				},
				y: {
					...defaultConfig.scales.y,
					min: minY,
					max: maxY,
					ticks: {
						...defaultConfig.scales.y.ticks,
						callback: ticksCallbackKG,
					},
				},
			},
		};

		setChartConfig(config);
	};

	return (
		<ChartModal
			data={data}
			loading={loading}
			tableData={tableData}
			labelsData={labelsData}
			closeChart={closeChart}
			chartConfig={chartConfig}
			chartPlugins={gradientBgPlugin}
			isOpen={isOpen}
		/>
	);
};

export default VO2Max;
